import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Badge,
  Avatar
} from '@mui/material';
import {
  Psychology as AIIcon,
  TrendingUp as TrendingUpIcon,
  Lightbulb as InsightIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as DealIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';

interface AIRecommendation {
  type: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  entity_id: string;
  entity_type: string;
  generated_by: string;
  action_items: string[];
}

interface InsightsTrends {
  period: string;
  insights_by_agent: Array<{
    agent: string;
    count: number;
  }>;
  insights_by_entity: {
    deals: number;
    contacts: number;
  };
  total_insights: number;
  recent_insights: Array<{
    id: string;
    generated_by_agent: string;
    insight_text: string;
    created_at: string;
    entity_type: string;
  }>;
}

export default function AIInsightsDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const queryClient = useQueryClient();

  // Fetch AI recommendations
  const { data: recommendations, isLoading: recommendationsLoading } = useQuery<{
    recommendations: AIRecommendation[];
    total_recommendations: number;
  }>({
    queryKey: ['aiRecommendations'],
    queryFn: () => baseClient.get('/insights/recommendations')
  });

  // Fetch insights trends
  const { data: trends, isLoading: trendsLoading } = useQuery<InsightsTrends>({
    queryKey: ['insightsTrends', selectedPeriod],
    queryFn: () => baseClient.get(`/insights/trends?period=${selectedPeriod}`)
  });

  // Generate insights mutations
  const generateDealInsightsMutation = useMutation({
    mutationFn: (dealId: string) => baseClient.post(`/insights/generate/deals/${dealId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insightsTrends'] });
    }
  });

  const generateContactInsightsMutation = useMutation({
    mutationFn: (contactId: string) => baseClient.post(`/insights/generate/contacts/${contactId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insightsTrends'] });
    }
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getAgentColor = (agent: string) => {
    switch (agent) {
      case 'CEO_AGENT': return '#1976d2';
      case 'CRO_AGENT': return '#388e3c';
      case 'CSO_AGENT': return '#f57c00';
      default: return '#757575';
    }
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'deal': return <DealIcon />;
      case 'contact': return <PersonIcon />;
      case 'account': return <BusinessIcon />;
      default: return <InsightIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (recommendationsLoading || trendsLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <AIIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            AI Insights & Recommendations
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            AI-powered insights and recommendations across your entire CRM
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['aiRecommendations'] });
              queryClient.invalidateQueries({ queryKey: ['insightsTrends'] });
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab 
            label={
              <Badge badgeContent={recommendations?.recommendations.length || 0} color="primary">
                Recommendations
              </Badge>
            } 
            icon={<AutoAwesomeIcon />} 
          />
          <Tab label="Insights Trends" icon={<TrendingUpIcon />} />
          <Tab label="AI Agents Activity" icon={<AIIcon />} />
        </Tabs>
      </Box>

      {/* Recommendations Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Priority Summary */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Priority Summary
                </Typography>
                {recommendations && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">High Priority</Typography>
                      <Chip 
                        label={recommendations.recommendations.filter(r => r.priority === 'high').length}
                        color="error"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Medium Priority</Typography>
                      <Chip 
                        label={recommendations.recommendations.filter(r => r.priority === 'medium').length}
                        color="warning"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Low Priority</Typography>
                      <Chip 
                        label={recommendations.recommendations.filter(r => r.priority === 'low').length}
                        color="info"
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Entity Breakdown */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Entity Breakdown
                </Typography>
                {recommendations && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DealIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">Deals</Typography>
                      </Box>
                      <Typography variant="body2">
                        {recommendations.recommendations.filter(r => r.entity_type === 'deal').length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">Contacts</Typography>
                      </Box>
                      <Typography variant="body2">
                        {recommendations.recommendations.filter(r => r.entity_type === 'contact').length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">Accounts</Typography>
                      </Box>
                      <Typography variant="body2">
                        {recommendations.recommendations.filter(r => r.entity_type === 'account').length}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AI Agents Activity */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Agents Activity
                </Typography>
                {recommendations && (
                  <Box>
                    {['CEO_AGENT', 'CRO_AGENT', 'CSO_AGENT'].map(agent => (
                      <Box key={agent} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              mr: 1, 
                              bgcolor: getAgentColor(agent),
                              fontSize: 10
                            }}
                          >
                            {agent.charAt(0)}
                          </Avatar>
                          <Typography variant="body2">{agent.replace('_', ' ')}</Typography>
                        </Box>
                        <Typography variant="body2">
                          {recommendations.recommendations.filter(r => r.generated_by === agent).length}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations List */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Recommendations
                </Typography>
                {recommendations && recommendations.recommendations.length > 0 ? (
                  <List>
                    {recommendations.recommendations.map((recommendation, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemIcon>
                            {getEntityIcon(recommendation.entity_type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2">
                                  {recommendation.title}
                                </Typography>
                                <Chip 
                                  label={recommendation.priority}
                                  color={getPriorityColor(recommendation.priority) as any}
                                  size="small"
                                />
                                <Chip 
                                  label={recommendation.generated_by.replace('_', ' ')}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'block' }}>
                                <Typography variant="body2" component="span" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  {recommendation.description}
                                </Typography>
                                {recommendation.action_items.length > 0 && (
                                  <Box component="span" sx={{ display: 'block' }}>
                                    <Typography variant="caption" component="span" color="primary" sx={{ fontWeight: 'bold', display: 'block' }}>
                                      Suggested Actions:
                                    </Typography>
                                    <Box component="span" sx={{ display: 'block', pl: 2 }}>
                                      {recommendation.action_items.map((item, idx) => (
                                        <Typography key={idx} variant="caption" component="span" sx={{ display: 'block' }}>• {item}</Typography>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recommendations.recommendations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">
                    No AI recommendations available at this time. The system will generate recommendations as you use the CRM.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Trends Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Insights by AI Agent
                </Typography>
                {trends && trends.insights_by_agent.length > 0 ? (
                  <List>
                    {trends.insights_by_agent.map((agent, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              bgcolor: getAgentColor(agent.agent),
                              width: 32,
                              height: 32
                            }}
                          >
                            {agent.agent.charAt(0)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={agent.agent.replace('_', ' ')}
                          secondary={`${agent.count} insights generated`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No insights data available for the selected period.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Insights
                </Typography>
                {trends && trends.recent_insights.length > 0 ? (
                  <List>
                    {trends.recent_insights.map((insight) => (
                      <ListItem key={insight.id} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon>
                          {getEntityIcon(insight.entity_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={insight.generated_by_agent.replace('_', ' ')}
                                size="small"
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(insight.created_at)}
                              </Typography>
                            </Box>
                          }
                          secondary={insight.insight_text}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No recent insights available.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* AI Agents Activity Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Agents Performance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive AI agent activity and performance metrics coming soon...
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
